import { useState } from "react";
import { Plus, Trash2, Check, Loader2, PackageCheck } from "lucide-react";
import api from "../lib/api";
import MasLogo from "../components/MasLogo";

export default function Kiosk() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [items, setItems] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const setItem = (i, v) => setItems((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  const addItem = () => setItems((arr) => [...arr, ""]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const reset = () => {
    setName(""); setPhone(""); setEmail(""); setItems([""]); setDone(null); setError("");
  };

  const submit = async () => {
    setError("");
    const cleanItems = items.map((s) => s.trim()).filter(Boolean);
    if (!name.trim() || !phone.trim() || cleanItems.length === 0) {
      setError("Please enter your name, phone number, and at least one item.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/convention/orders", { customer_name: name, phone, email, items: cleanItems });
      setDone(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please ask a MAS team member for help.");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full border-2 border-neutral-200 rounded-2xl px-5 h-16 text-xl focus:border-[#0A0A0A] outline-none transition-colors";

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-6 text-center">
        <div className="w-24 h-24 rounded-3xl bg-white text-[#0A0A0A] flex items-center justify-center mb-8">
          <PackageCheck size={48} />
        </div>
        <h1 className="font-display text-5xl tracking-tight" style={{ fontWeight: 900 }}>You're all set!</h1>
        <p className="text-white/60 text-xl mt-4">Order <strong className="text-white">{done.order_no}</strong></p>
        <p className="text-white/60 text-lg mt-2 max-w-md">Please hand your item(s) to the MAS team. We'll text you when grading starts and again when your card is ready for pickup.</p>
        <button data-testid="kiosk-next-customer" onClick={reset} className="mt-10 px-10 py-5 bg-white text-[#0A0A0A] text-xl font-bold rounded-full active:scale-95 transition-transform">
          Start New Drop-Off
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="bg-[#0A0A0A] py-6 flex justify-center"><MasLogo tone="light" size="lg" /></div>
      <div className="flex-1 max-w-2xl w-full mx-auto px-6 py-8">
        <h1 className="font-display text-4xl tracking-tight text-[#0A0A0A]" style={{ fontWeight: 900 }}>On-Site Grading Drop-Off</h1>
        <p className="text-neutral-500 text-lg mt-2 mb-8">Enter your details and the item(s) you're dropping off to grade.</p>

        <label className="text-sm font-bold uppercase tracking-widest text-neutral-500">Full Name</label>
        <input data-testid="kiosk-name" className={`${input} mt-2 mb-5`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />

        <label className="text-sm font-bold uppercase tracking-widest text-neutral-500">Phone Number (for text updates)</label>
        <input data-testid="kiosk-phone" className={`${input} mt-2 mb-5`} keyboardType="phone-pad" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />

        <label className="text-sm font-bold uppercase tracking-widest text-neutral-500">Email (for status updates)</label>
        <input data-testid="kiosk-email" className={`${input} mt-2 mb-6`} inputMode="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />

        <label className="text-sm font-bold uppercase tracking-widest text-neutral-500">Item(s) to Grade</label>
        <div className="mt-2 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3">
              <input data-testid={`kiosk-item-${i}`} className={`${input} flex-1`} value={it} onChange={(e) => setItem(i, e.target.value)} placeholder={`e.g. 2003 Topps LeBron James #221`} />
              {items.length > 1 && (
                <button data-testid={`kiosk-remove-item-${i}`} onClick={() => removeItem(i)} className="w-14 h-16 shrink-0 rounded-2xl border-2 border-neutral-200 flex items-center justify-center text-red-500 active:scale-95"><Trash2 size={22} /></button>
              )}
            </div>
          ))}
        </div>
        <button data-testid="kiosk-add-item" onClick={addItem} className="mt-3 inline-flex items-center gap-2 text-[#0A0A0A] font-bold text-lg"><Plus size={20} /> Add another item</button>

        {error ? <p className="text-red-600 font-semibold mt-6">{error}</p> : null}

        <button data-testid="kiosk-submit" onClick={submit} disabled={submitting} className="mt-8 w-full h-18 py-5 bg-[#0A0A0A] text-white text-2xl font-bold rounded-full flex items-center justify-center gap-3 active:scale-[0.99] transition-transform disabled:opacity-50">
          {submitting ? <Loader2 className="animate-spin" /> : <Check size={26} />} Submit Drop-Off
        </button>
      </div>
    </div>
  );
}
